// src/lib/generateQuoteOrderPDF.js
// 見積書と注文請書を合体した「見積書 兼 注文書」1枚もの
// generateInvoicePDF.js のレイアウト・スタイル・処理は同一。タイトルと、発行日＋有効期限の表示が違い
// html2canvas + jsPDF方式：HTMLをそのままキャプチャしてPDF化
// 日本語フォントの外部ダウンロード不要・複数案件まとめ見積対応

import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const formatDate = (dateStr) => {
  if (!dateStr) return '未設定';
  const d = new Date(dateStr);
  return `${d.getFullYear()}年${d.getMonth()+1}月${d.getDate()}日`;
};

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '\u00a50';
  return '\u00a5' + Number(amount).toLocaleString('ja-JP');
};

export async function generateQuoteOrderPDF(est, profile) {
  const linkedProjects = est.linked_projects || [];
  const linkedExpenses = est.linked_expenses || [];
  const hasLinkedProjects = linkedProjects.length > 0;
  const taxIncluded = est.tax_included !== false;

  const projectAmount = hasLinkedProjects
    ? linkedProjects.reduce((s, p) => s + (p.amount || 0), 0)
    : (est.amount || 0);
  const expenseAmount = linkedExpenses.reduce((s, e) => s + (e.amount || 0), 0);

  // 実働金額（経費を含まない）
  const workAmount = hasLinkedProjects
    ? projectAmount
    : (projectAmount - expenseAmount);

  // 消費税は実働部分にのみ適用（経費は非課税）
  const tax = taxIncluded ? Math.floor(workAmount * 0.1) : 0;
  const total = workAmount + tax + expenseAmount;

  // detailRows用（単体見積の明細金額表示）
  const amount = workAmount;

  const toCompany = est.to_company || est.clients?.name || '';
  const toContact = est.to_contact || est.clients?.contact_name || '';

  const qualifiedNo = profile?.qualified_invoice_no || '';

  // 番号は estimate_no 優先 → 無ければ請求側にフォールバック（現行データそのまま渡してもOK）
  const quoteNo = est.estimate_no || est.invoice_no || 'N/A';
  // 発行日は見積側の発行日を優先（無ければ請求側の発行日にフォールバック）
  const issuedDate = est.estimate_issued_date || est.issued_date;
  const validUntil = est.valid_until || est.due_date;

  // ── 明細行HTML生成 ──
  const detailRows = hasLinkedProjects
    ? linkedProjects.map((p, i) => `
        <tr style="background:${i % 2 === 0 ? '#f8fafc' : '#fff'};">
          <td style="padding:10px 14px; color:#1e293b;">
            <div>${p.title || '案件'}</div>
            ${p.due_date ? `<div style="font-size:11px; color:#64748b; margin-top:2px;">${p.due_date}</div>` : ''}
            ${p.memo ? `<div style="font-size:11px; color:#64748b; margin-top:2px;">${p.memo}</div>` : ''}
          </td>
          <td style="padding:10px 14px; text-align:right; color:#1e293b; vertical-align:top;">${formatCurrency(p.amount)}</td>
        </tr>
      `).join('')
    : `
        <tr style="background:#f8fafc;">
          <td style="padding:10px 14px; color:#1e293b;">${est.title || '-'}</td>
          <td style="padding:10px 14px; text-align:right; color:#1e293b;">${formatCurrency(amount)}</td>
        </tr>
      `;

  const expenseRows = linkedExpenses.length > 0
    ? linkedExpenses.map((e, i) => `
        <tr style="background:${i % 2 === 0 ? '#f0f9ff' : '#f8fafc'};">
          <td style="padding:10px 14px; color:#475569;">
            <div>【経費】${e.category}</div>
            ${e.date ? `<div style="font-size:11px; color:#94a3b8; margin-top:2px;">${e.date}</div>` : ''}
            ${e.memo ? `<div style="font-size:11px; color:#94a3b8; margin-top:2px;">${e.memo}</div>` : ''}
          </td>
          <td style="padding:10px 14px; text-align:right; color:#475569; vertical-align:top;">${formatCurrency(e.amount)}</td>
        </tr>
      `).join('')
    : '';

  const signature = profile?.signature || '';

  const container = document.createElement('div');
  container.style.cssText = `
    position: fixed; left: -9999px; top: 0;
    width: 794px; background: #ffffff;
    font-family: 'Hiragino Kaku Gothic ProN','Yu Gothic','Meiryo',sans-serif;
    padding-bottom: 200px;
  `;

  container.innerHTML = `
    <div style="width:794px; background:#fff; padding:0; font-family:'Hiragino Kaku Gothic ProN','Yu Gothic','Meiryo',sans-serif;">

      <!-- ヘッダー：見積書 兼 注文書 中央 -->
      <div style="padding:40px 40px 24px; text-align:center; border-bottom:2px solid #1e293b;">
        <div style="font-size:26px; font-weight:800; color:#1e293b; letter-spacing:4px;">見積書 兼 注文書</div>
      </div>

      <!-- 発行情報行 -->
      <div style="padding:16px 40px; display:flex; justify-content:space-between; align-items:flex-start; border-bottom:1px solid #e2e8f0;">
        <!-- 宛先 -->
        <div>
          ${toCompany ? `<div style="font-size:16px; font-weight:700; color:#1e293b;">${toCompany} 御中</div>` : ''}
          ${toContact ? `<div style="font-size:13px; color:#475569; margin-top:2px;">${toContact} 様</div>` : ''}
          ${!toCompany && !toContact ? `<div style="font-size:14px; color:#94a3b8;">宛先未設定</div>` : ''}
        </div>
        <!-- 番号・日付（発行日＋有効期限） -->
        <div style="text-align:right; font-size:13px; color:#475569; line-height:1.8;">
          <div>見積番号：<span style="font-weight:700; color:#1e293b;">${quoteNo}</span></div>
          <div>発行日：${formatDate(issuedDate)}</div>
          <div>有効期限：${formatDate(validUntil)}</div>
        </div>
      </div>

      <!-- 本文 -->
      <div style="padding:28px 40px 36px;">

        <!-- 件名 -->
        <div style="font-size:18px; font-weight:700; color:#1e293b; margin-bottom:6px;">${est.title || '無題'}</div>
        ${hasLinkedProjects ? `<div style="font-size:12px; color:#64748b; margin-bottom:8px;">対象案件：${linkedProjects.length}件</div>` : ''}

        <!-- お見積金額ブロック -->
        <div style="margin:16px 0 24px;">
          <table style="border:1px solid #e2e8f0; border-radius:8px; border-collapse:separate; background:#f8fafc; padding:0;">
            <tr>
              <td style="padding:16px 24px;">
                <div style="font-size:12px; color:#64748b; margin-bottom:6px; white-space:nowrap;">お見積金額${taxIncluded ? '（税込）' : '（税抜）'}</div>
                <div style="font-size:32px; font-weight:800; color:#1e293b; white-space:nowrap; line-height:1.2;">${formatCurrency(total)}</div>
              </td>
            </tr>
          </table>
        </div>

        <!-- 明細テーブル -->
        <table style="width:100%; border-collapse:collapse; margin-bottom:28px; font-size:13px;">
          <thead>
            <tr style="background:#1e293b; color:#fff;">
              <th style="padding:10px 14px; text-align:left; font-weight:600;">項目</th>
              <th style="padding:10px 14px; text-align:right; font-weight:600;">金額</th>
            </tr>
          </thead>
          <tbody>
            ${detailRows}
            ${expenseRows}
            <tr style="border-top:1px solid #e2e8f0;">
              <td style="padding:10px 14px; color:#1e293b;">実働小計（税抜）</td>
              <td style="padding:10px 14px; text-align:right; color:#1e293b;">${formatCurrency(workAmount)}</td>
            </tr>
            ${taxIncluded ? `
            <tr style="background:#f8fafc;">
              <td style="padding:10px 14px; color:#1e293b;">消費税（10%）</td>
              <td style="padding:10px 14px; text-align:right; color:#1e293b;">${formatCurrency(tax)}</td>
            </tr>
            ` : `
            <tr style="background:#f8fafc;">
              <td style="padding:10px 14px; color:#64748b;">消費税</td>
              <td style="padding:10px 14px; text-align:right; color:#64748b;">非課税・免税</td>
            </tr>
            `}
            ${expenseAmount > 0 ? `
            <tr style="background:#f0f9ff;">
              <td style="padding:10px 14px; color:#475569;">経費計（消費税対象外）</td>
              <td style="padding:10px 14px; text-align:right; color:#475569;">${formatCurrency(expenseAmount)}</td>
            </tr>
            ` : ''}
            <tr style="background:#1e293b; color:#fff;">
              <td style="padding:12px 14px; font-weight:700;">合計${taxIncluded ? '（税込）' : ''}</td>
              <td style="padding:12px 14px; text-align:right; font-weight:700;">${formatCurrency(total)}</td>
            </tr>
          </tbody>
        </table>

        <!-- メモ・クライアント署名欄・発行者欄を横並び or 縦並び -->
        <div style="display:flex; gap:20px; align-items:flex-start; flex-wrap:wrap;">

          <!-- 左：メモ＋クライアント記入欄（振込先の位置に配置） -->
          <div style="flex:1; min-width:240px;">
            ${est.memo ? `
            <div style="margin-bottom:16px;">
              <div style="font-size:11px; font-weight:700; color:#64748b; margin-bottom:6px;">備考</div>
              <div style="font-size:12px; color:#334155; line-height:1.7; background:#f8fafc; border-radius:6px; padding:10px 12px;">${est.memo.replace(/\n/g, '<br>')}</div>
            </div>
            ` : ''}
            <!-- クライアント記入欄（ご注文者がご記入） -->
            <div style="border:1px solid #cbd5e1; border-radius:8px; padding:16px 18px;">
              <div style="font-size:11px; font-weight:700; color:#1e293b; margin-bottom:4px;">ご注文記入欄</div>
              <div style="font-size:10px; color:#94a3b8; margin-bottom:14px;">本欄にご記入のうえご返送いただくことで、ご注文の確定とさせていただきます。</div>
              <div style="display:flex; align-items:flex-end; gap:8px; margin-bottom:16px;">
                <span style="font-size:11px; color:#475569; white-space:nowrap; width:52px;">日付</span>
                <span style="flex:1; border-bottom:1px solid #94a3b8; height:16px;"></span>
              </div>
              <div style="display:flex; align-items:flex-end; gap:8px; margin-bottom:16px;">
                <span style="font-size:11px; color:#475569; white-space:nowrap; width:52px;">ご住所</span>
                <span style="flex:1; border-bottom:1px solid #94a3b8; height:16px;"></span>
              </div>
              <div style="display:flex; align-items:flex-end; gap:8px; margin-bottom:16px;">
                <span style="font-size:11px; color:#475569; white-space:nowrap; width:52px;">会社名</span>
                <span style="flex:1; border-bottom:1px solid #94a3b8; height:16px;"></span>
              </div>
              <div style="display:flex; align-items:flex-end; gap:8px;">
                <span style="font-size:11px; color:#475569; white-space:nowrap; width:52px;">ご氏名</span>
                <span style="flex:1; border-bottom:1px solid #94a3b8; height:16px;"></span>
              </div>
            </div>
          </div>

          <!-- 右：発行者欄（そのまま） -->
          ${(signature || qualifiedNo) ? `
          <div style="min-width:200px; border:1px solid #e2e8f0; border-radius:8px; padding:14px 16px;">
            <div style="font-size:11px; font-weight:700; color:#64748b; margin-bottom:8px;">発行者</div>
            ${signature ? `<div style="font-size:12px; color:#1e293b; line-height:1.9;">${signature.replace(/\n/g, '<br>')}</div>` : ''}
            ${qualifiedNo ? `<div style="font-size:11px; color:#475569; line-height:1.7; margin-top:${signature ? '8px' : '0'}; padding-top:${signature ? '8px' : '0'}; border-top:${signature ? '1px solid #f1f5f9' : 'none'};">登録番号：${qualifiedNo}</div>` : ''}
          </div>
          ` : ''}

        </div>

      </div>

      <!-- フッター -->
      <div style="background:#f8fafc; border-top:1px solid #e2e8f0; padding:10px 40px; text-align:right;">
        <div style="font-size:10px; color:#94a3b8;">${new Date().toLocaleDateString('ja-JP', { year:'numeric', month:'long', day:'numeric' })} 発行</div>
      </div>

    </div>
  `;

  document.body.appendChild(container);

  try {
    const canvas = await html2canvas(container, {
      scale: 2, useCORS: true, backgroundColor: '#ffffff', logging: false,
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pdfW = 210;
    const pdfH = (canvas.height * pdfW) / canvas.width;
    const pageH = 297;

    if (pdfH <= pageH) {
      // 1ページに収まる場合はそのまま
      pdf.addImage(imgData, 'PNG', 0, 0, pdfW, pdfH);
    } else {
      // 収まらない場合：全体を縮小して1ページに収める
      const scale = pageH / pdfH;
      const scaledW = pdfW * scale;
      const offsetX = (pdfW - scaledW) / 2;
      pdf.addImage(imgData, 'PNG', offsetX, 0, scaledW, pageH);
    }

    const safeName = (est.title || 'quote-order').replace(/[<>:"/\\|?*]/g, '_').slice(0, 30);
    pdf.save(`${quoteNo !== 'N/A' ? quoteNo : 'quote-order'}_${safeName}.pdf`);

  } finally {
    document.body.removeChild(container);
  }
}
